import { useEffect } from 'react';
import styled from 'styled-components';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Button from 'design-system/src/components/Button/Button';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import type { AllFormData, MoreInfoFormData } from '../../../types.ts';
import { FormLabel } from '../../FormLabel';
import { Input } from '../../Input';
import { Dropdown, DropdownContainer } from '../../Dropdown.tsx';
import { FormContainer } from '../FormContainer/FormContainer';
import { getSpecialtyOptions, getEhrOptions } from '../../signup/utils.tsx';
import { requiredErrorMessage } from '../../../utils.tsx';
import { ControllerWrapper } from '../../signup/ControllerWrapper.tsx';

const StyledSignupForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export type MoreInfoFormProps = {
  onBack: () => void;
  onContinue: (formData: MoreInfoFormData) => void;
  onBeforeUnload: (formData: MoreInfoFormData) => void;
  // For prefilling the form with data if the user navigates to another step and then comes back
  formData: AllFormData;
};

/**
 * Form collecting less basic but still useful information for a provider to sign up.
 */
export const MoreInfoForm = ({ onBack, onContinue, onBeforeUnload, formData }: MoreInfoFormProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<MoreInfoFormData>({ defaultValues: formData });
  const [specialtyValue, ehrValue] = useWatch({ control, name: ['specialty', 'ehr'] });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      onBeforeUnload(getValues());
      // We need to return a truthy value to get the browser to show the alert
      // that tries to persuade the user to stay and fill out the form
      return true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [getValues, onBeforeUnload]);

  return (
    <FormContainer title="Tell us about your practice" showExtraContent={true} onBack={onBack}>
      <StyledSignupForm onSubmit={handleSubmit(onContinue)}>
        <ControllerWrapper>
          <Controller
            control={control}
            name="practice_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Provider Entity"
                hasError={!!errors.practice_name}
                placeholder="e.g., New York Specialty Partners, LLC."
                helperText="Provide the full legal name of your practice as used on official documents. Avoid abbreviations or informal variations."
                {...field}
              />
            )}
          />
          {errors.practice_name && requiredErrorMessage}
        </ControllerWrapper>

        <Controller
          name="specialty"
          control={control}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id="specialty-label">Specialty</FormLabel>
              <Dropdown labelId="specialty-label" id="specialty" label="Specialty" {...field}>
                {getSpecialtyOptions()}
              </Dropdown>
            </DropdownContainer>
          )}
        />

        {specialtyValue && specialtyValue.includes('other') && (
          <Controller
            control={control}
            name="specify_other_specialty"
            rules={{ required: false }}
            render={({ field }) => <Input label="Specify other specialty" {...field} />}
          />
        )}

        <ControllerWrapper>
          <Controller
            name="size_numeric"
            control={control}
            rules={{
              required: { value: true, message: 'Number of providers is required' },
              min: { value: 1, message: 'Must have at least 1 provider' },
              max: { value: 10000, message: 'Number of providers must be reasonable' },
            }}
            render={({ field }) => (
              <Input type="number" label="Number of providers" hasError={!!errors.size_numeric} {...field} />
            )}
          />
          {errors.size_numeric && <ErrorMessage>{errors.size_numeric.message}</ErrorMessage>}
        </ControllerWrapper>

        <Controller
          name="ehr"
          control={control}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id="ehr-label">EHR</FormLabel>
              <Dropdown labelId="ehr-label" id="ehr" label="EHR" {...field}>
                {getEhrOptions()}
              </Dropdown>
            </DropdownContainer>
          )}
        />

        {ehrValue === 'other' && (
          <Controller
            control={control}
            name="specify_other_ehr"
            render={({ field }) => <Input label="Specify other EHR" {...field} />}
          />
        )}

        <Button size="lg" width="full" type="submit">
          Continue
        </Button>
      </StyledSignupForm>
    </FormContainer>
  );
};
